import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import DocumentEditor from 'notebook/documents/DocumentEditor';
import EntryExportConfig from 'notebook/entries/exports/EntryExportConfig';
import {ReviewHistory} from 'notebook/entries/exports/EntryExportMetadata';
import ReviewHistoryContainer from 'notebook/entries/exports/ReviewHistoryContainer';
import {editorPropertiesContextForPrintView} from 'notebook/entries/PrinterFriendlyViewUtils';
import PrintableWrapper from 'notebook/mediocre/print/PrintableWrapper';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import {isReviewHistoryItem, ReviewHistoryItem} from 'notebook/reviews/ReviewConstants';
import {shouldShowNewReviewHistory} from 'notebook/reviews/ReviewUtil';
import {usePMEIsAndMethodTasks, useSelectStepTasks} from 'procedures/worksheets/ProcedureWorksheetContext';
import ProcedureWorksheetPrinterFriendlyHeader from 'procedures/worksheets/ProcedureWorksheetPrinterFriendlyHeader';
import {multiLineEllipsisText} from 'styles/Utilities';
import getTestProps from 'test/lib/cypress/getTestProps';

import {getSelectedStepControlFlags} from '../util/worksheetSelectedStepControl';
import {Worksheet, WorksheetTaskStatusType} from '../WorksheetConstants';
import {getWorksheetContextFallback, WorksheetContext, WorksheetContextValue} from '../WorksheetContext';
import WorksheetPrinterFriendlyStepHeader from './WorksheetPrinterFriendlyStepHeader';
import WorksheetPrinterFriendlyStepsList from './WorksheetPrinterFriendlyStepsList';
import {extractMethodTasksData, extractStepTasksData} from './WorksheetUtils';

interface WorksheetPrinterFriendlyLayoutProps {
  worksheet: Worksheet;
  exportConfig?: EntryExportConfig;
}

export const WORKSHEET_PRINTER_FRIENDLY_DOCUMENT_EDITOR_PROPS = {
  locked: true,
  disableInternalAutoSaver: true,
  viewer: true,
  disableFormatting: true,
  disablePerformanceIndicator: true,
} as const;

export function getWorksheetPrinterFriendlyEditorPropertiesContext(
  documentId: string
): ReturnType<typeof editorPropertiesContextForPrintView> {
  return editorPropertiesContextForPrintView(documentId, true);
}

export default function WorksheetPrinterFriendly({
  worksheet,
  exportConfig,
}: WorksheetPrinterFriendlyLayoutProps): JSX.Element {
  const pmeisAndTasks = usePMEIsAndMethodTasks();
  const selectStepTasks = useSelectStepTasks();
  const worksheetTaskData = extractMethodTasksData(pmeisAndTasks);
  const isWorksheetPending =
    !worksheetTaskData.status || worksheetTaskData.status === WorksheetTaskStatusType.PENDING;

  const stepGroups = worksheet.worksheetStepGroups.map((stepGroup, stepGroupIndex) => {
    const steps = stepGroup.worksheetSteps.map((step, stepIndex) => {
      const stepTaskData = extractStepTasksData(selectStepTasks(step.tasks));

      const {isSelectedStepActionsLocked} = getSelectedStepControlFlags(
        isWorksheetPending,
        stepGroupIndex,
        stepTaskData.status,
        worksheet.worksheetStepGroups.length
      );

      // We want the displayed status of step belonging to a "Run" group  to be "Not Ready", if the steps in "Pre-run" group
      // are not yet finalized.
      const stepStatus = isSelectedStepActionsLocked
        ? WorksheetTaskStatusType.NOT_READY
        : stepTaskData.status;

      return (
        <div key={`stepGroup-${stepGroupIndex}-${stepIndex}`} className="stepgroup-container">
          <WorksheetPrinterFriendlyStepHeader
            name={step.document.name}
            status={stepStatus}
            lastEditedUser={stepTaskData.lastEditedUser}
            timestamp={stepTaskData.timestamp}
          />
          <div className={css(styles.editorContainer)}>
            <EditorPropertiesContext.Provider
              value={getWorksheetPrinterFriendlyEditorPropertiesContext(step.document.id)}
            >
              <DocumentEditor
                {...getTestProps({component: 'WorksheetPrinterFriendly', element: 'editor'})}
                id={step.document.id}
                {...WORKSHEET_PRINTER_FRIENDLY_DOCUMENT_EDITOR_PROPS}
              />
            </EditorPropertiesContext.Provider>
          </div>
        </div>
      );
    });
    return (
      <div key={`stepGroup-${stepGroupIndex}-${stepGroup.name}`} className="section">
        <hr />
        <h2>{stepGroup.name}</h2>
        {steps}
      </div>
    );
  });
  const worksheetContextValue: WorksheetContextValue = useMemo(
    () => ({
      ...getWorksheetContextFallback(),
      deprecatedStepCreationContextId: null,
      worksheetTaskData,
      worksheet,
      viewer: true,
      isExecutionEnabled: false,
    }),
    [worksheetTaskData, worksheet]
  );

  const worksheetReviewHistoryItems: Array<ReviewHistoryItem> =
    worksheet?.review?.history?.filter((item): item is ReviewHistoryItem => isReviewHistoryItem(item)) ?? [];

  const showNewReviewHistory = shouldShowNewReviewHistory(worksheetReviewHistoryItems);

  const worksheetItemHasReviewLifecycleInfo = worksheetReviewHistoryItems.some(
    (item) => item.reviewLifecycleInfo != null
  );

  return (
    <PrintableWrapper title={worksheet.name} shouldPadForTimestamps={false}>
      <WorksheetContext.Provider value={worksheetContextValue}>
        <div className="printable-page-container printable-worksheet">
          <ProcedureWorksheetPrinterFriendlyHeader worksheet={worksheet} />
          {BenchlingConfig.ELN_EXPORT_PDF_CONFIG.includeReviewHistoryTable &&
            worksheetReviewHistoryItems.length > 0 && (
              <div className="mediocre">
                {showNewReviewHistory ? (
                  <>
                    <ReviewHistoryContainer
                      reviewHistoryItems={worksheetReviewHistoryItems}
                      showReviewCommentHistory={
                        BenchlingConfig.ELN_EXPORT_PDF_CONFIG.includeCommentsInReviewHistoryTable
                      }
                      reviewableId={worksheet.id}
                    />
                    <div className="title">
                      <h1
                        {...getTestProps({
                          component: 'ProcedureWorksheetPrinterFriendlyHeader',
                          element: 'name',
                        })}
                      >
                        {worksheet?.name}
                      </h1>
                    </div>
                  </>
                ) : (
                  <ReviewHistory
                    items={worksheetReviewHistoryItems}
                    showReviewCommentHistory={
                      BenchlingConfig.ELN_EXPORT_PDF_CONFIG.includeCommentsInReviewHistoryTable
                    }
                    showReviewLifecycleColumns={worksheetItemHasReviewLifecycleInfo}
                    reviewableId={worksheet.id}
                  />
                )}
              </div>
            )}
          <WorksheetPrinterFriendlyStepsList
            stepGroups={worksheet.worksheetStepGroups}
            isWorksheetPending={isWorksheetPending}
            selectStepTasks={selectStepTasks}
          />
          <div className={css(styles.container)}>{stepGroups}</div>
        </div>
      </WorksheetContext.Provider>
    </PrintableWrapper>
  );
}

const styles = StyleSheet.create({
  container: {display: 'flex', flexDirection: 'column'},
  editorContainer: {flex: 1, width: '100%', margin: '10px'},
  stepGroupName: {
    fontSize: '20px',
    fontWeight: 'bold',
    ...multiLineEllipsisText(1),
    paddingLeft: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
});
